import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { Ballast } from "../../../../../http/border/types/ballasts";
import { Base, BaseTypeValue } from "../../../../../http/border/types/bases";
import { Substructure } from "../../../../../http/border/types/substructures";
import { AdminBorderSystemNormative } from "../../../../../http/border/types/systemNormatives";
import {
  AdminBorderUprightSpanMapping,
  UpsertBorderUprightSpanMappingBody,
} from "../../../../../http/border/types/uprightSpanMappings";
import { Upright } from "../../../../../http/border/types/uprights";
import { useBallastsApi } from "../../../../../http/border/useBallasts";
import { useBasesApi } from "../../../../../http/border/useBases";
import { useSubstructuresApi } from "../../../../../http/border/useSubstructures";
import { useSystemNormativesApi } from "../../../../../http/border/useSystemNormatives";
import { useUprightSpanMappingsApi } from "../../../../../http/border/useUprightSpanMappings";
import { useUprightsApi } from "../../../../../http/border/useUprights";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface UprightSpanMappingFormBody {
  systemNormative: AdminBorderSystemNormative | null;
  upright: Upright | null;
  ballast: Ballast | null;
  nrBallastTerminal: number | null;
  nrBallastInt: number | null;
  substructure: Substructure | null;
  base: Base | null;
  nrRivets: number | null;
  span: number | null;
}

interface UprightSpanMappingFormProps {
  uprightSpanMapping?: AdminBorderUprightSpanMapping;
  onClose: () => void;
  isDisabled?: boolean;
}

const toUprightOption = (uprightSpanMapping?: AdminBorderUprightSpanMapping): Upright | null =>
  uprightSpanMapping?.upright
    ? {
        ...uprightSpanMapping.upright,
        lowerHoleDiameter: uprightSpanMapping.upright.lowerHoleDiameter ?? undefined,
        uprightCap: undefined,
      }
    : null;

const toSubstructureOption = (uprightSpanMapping?: AdminBorderUprightSpanMapping): Substructure | null =>
  uprightSpanMapping?.substructure
    ? {
        ...uprightSpanMapping.substructure,
        nameIt: null,
        nameDe: null,
        nameEs: null,
        nameFr: null,
      }
    : null;

const toBaseOption = (uprightSpanMapping?: AdminBorderUprightSpanMapping): Base | null =>
  uprightSpanMapping?.base
    ? {
        ...uprightSpanMapping.base,
        type: uprightSpanMapping.base.type as BaseTypeValue,
        baseOrientation: uprightSpanMapping.base.baseOrientation ?? undefined,
        corrugationSpan: uprightSpanMapping.base.corrugationSpan ?? undefined,
        waterproofKit: undefined,
      }
    : null;

const UprightSpanMappingForm: FC<UprightSpanMappingFormProps> = ({
  uprightSpanMapping,
  onClose,
  isDisabled,
}: UprightSpanMappingFormProps) => {
  const i18n = useI18n();
  const { borderSystemId } = useParams<{ borderSystemId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultSystemNormativeOptions, setDefaultSystemNormativeOptions] = useState<AdminBorderSystemNormative[]>([]);
  const [defaultUprightOptions, setDefaultUprightOptions] = useState<Upright[]>([]);
  const [defaultBallastOptions, setDefaultBallastOptions] = useState<Ballast[]>([]);
  const [defaultSubstructureOptions, setDefaultSubstructureOptions] = useState<Substructure[]>([]);
  const [defaultBaseOptions, setDefaultBaseOptions] = useState<Base[]>([]);

  const systemNormativesApi = useSystemNormativesApi();
  const uprightsApi = useUprightsApi();
  const ballastsApi = useBallastsApi();
  const substructuresApi = useSubstructuresApi();
  const basesApi = useBasesApi();
  const uprightSpanMappingsApi = useUprightSpanMappingsApi();

  const defaultValues: UprightSpanMappingFormBody = {
    systemNormative: uprightSpanMapping?.systemNormative ?? null,
    upright: toUprightOption(uprightSpanMapping),
    ballast: uprightSpanMapping?.ballast ?? null,
    nrBallastTerminal: uprightSpanMapping?.nrBallastTerminal ?? null,
    nrBallastInt: uprightSpanMapping?.nrBallastInt ?? null,
    substructure: toSubstructureOption(uprightSpanMapping),
    base: toBaseOption(uprightSpanMapping),
    nrRivets: uprightSpanMapping?.nrRivets ?? null,
    span: uprightSpanMapping?.span ?? null,
  };

  const onSubmit = () => async (formData: UprightSpanMappingFormBody) => {
    setIsSubmitting(true);

    const body: UpsertBorderUprightSpanMappingBody = {
      systemNormativeId: formData.systemNormative?.id!,
      uprightId: formData.upright?.item.id!,
      ballastId: formData.ballast?.item.id ?? null,
      nrBallastTerminal: formData.ballast ? formData.nrBallastTerminal : null,
      nrBallastInt: formData.ballast ? formData.nrBallastInt : null,
      substructureId: formData.substructure?.id ?? null,
      baseId: formData.base?.item.id ?? null,
      nrRivets: formData.nrRivets,
      span: formData.span!,
    };

    try {
      if (uprightSpanMapping?.id) {
        await uprightSpanMappingsApi.updateAdminUprightSpanMapping(borderSystemId!, uprightSpanMapping.id, body);
      } else {
        await uprightSpanMappingsApi.insertAdminUprightSpanMapping(borderSystemId!, body);
      }
      onClose();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<UprightSpanMappingFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={onSubmit}
    >
      {({ control, setValue, watch, reset }) => {
        const selectedBallast = watch("ballast");

        useEffect(() => {
          reset(defaultValues);
        }, [uprightSpanMapping]);

        useEffect(() => {
          if (!selectedBallast) {
            setValue("nrBallastTerminal", null);
            setValue("nrBallastInt", null);
          }
        }, [selectedBallast?.item.id]);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="systemNormative"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.normatives.singular} error={fieldState.error}>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());
                    const loadOptions = createLoadOptions(aborter, searchTerm, (query, signal) =>
                      systemNormativesApi.getAdminSystemNormatives(borderSystemId!, query, signal),
                    );

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultSystemNormativeOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultSystemNormativeOptions}
                        isDisabled={isDisabled || isSubmitting}
                        loadOptions={loadOptions}
                        getOptionLabel={(x) => x.normative.name}
                        getOptionValue={(x) => x.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("systemNormative", value)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <Controller
              name="upright"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.uprights.singular} error={fieldState.error}>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());
                    const loadOptions = createLoadOptions(aborter, searchTerm, uprightsApi.getAdminUprights);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultUprightOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultUprightOptions}
                        isDisabled={isDisabled || isSubmitting}
                        loadOptions={loadOptions}
                        getOptionLabel={(x) => itemLabel(x.item)}
                        getOptionValue={(x) => x.item.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("upright", value)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <Controller
              name="ballast"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.ballasts.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());
                    const loadOptions = createLoadOptions(aborter, searchTerm, ballastsApi.getAdminBallasts);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultBallastOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultBallastOptions}
                        isClearable
                        isDisabled={isDisabled || isSubmitting}
                        loadOptions={loadOptions}
                        getOptionLabel={(x) => itemLabel(x.item)}
                        getOptionValue={(x) => x.item.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("ballast", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <Controller
              name="nrBallastTerminal"
              control={control}
              rules={{ required: !!selectedBallast }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.common.nrBallastTerminal}
                  error={fieldState.error}
                  isOptional={!selectedBallast}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isDisabled || isSubmitting || !selectedBallast}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("nrBallastTerminal", value)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="nrBallastInt"
              control={control}
              rules={{ required: !!selectedBallast }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.common.nrBallastIntermediate}
                  error={fieldState.error}
                  isOptional={!selectedBallast}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isDisabled || isSubmitting || !selectedBallast}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("nrBallastInt", value)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="substructure"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.substructures.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());
                    const loadOptions = createLoadOptions(aborter, searchTerm, substructuresApi.getAdminSubstructures);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultSubstructureOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultSubstructureOptions}
                        isClearable
                        isDisabled={isDisabled || isSubmitting}
                        loadOptions={loadOptions}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("substructure", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <Controller
              name="base"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.bases.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());
                    const loadOptions = createLoadOptions(aborter, searchTerm, basesApi.getAdminBases);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultBaseOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultBaseOptions}
                        isClearable
                        isDisabled={isDisabled || isSubmitting}
                        loadOptions={loadOptions}
                        getOptionLabel={(x) => itemLabel(x.item)}
                        getOptionValue={(x) => x.item.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("base", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <Controller
              name="nrRivets"
              control={control}
              rules={{
                validate: (value) =>
                  value === null ||
                  value === undefined ||
                  value === 4 ||
                  value === 6 ||
                  i18n.translation.common.fieldInvalidValue,
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.nrRivets} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isDisabled || isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("nrRivets", value)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="span"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.span} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isDisabled || isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      suffix=" mm"
                      allowNegative={false}
                      onBlur={(value) => setValue("span", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default UprightSpanMappingForm;
